import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { scGray2 } from 'constants/systemColors';

export const ExpandableCell = ({ value, field }) => {
  const apiRef = useGridApiContext();
  const [expanded, setExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const contentRef = useRef(null);
  const fullContentRef = useRef(null);
  const content = useMemo(() => (value ? value : '—'), [value]);

  useEffect(() => {
    const hiddenDiv = document.createElement('div');
    hiddenDiv.style.lineHeight = '20px';
    hiddenDiv.style.fontSize = '14px';
    hiddenDiv.style.letterSpacing = '0.01071em';
    hiddenDiv.style.fontFamily = 'Roboto';
    hiddenDiv.style.position = 'absolute';
    hiddenDiv.style.visibility = 'hidden';
    hiddenDiv.style.width = contentRef.current?.offsetWidth + 'px';
    hiddenDiv.style.whiteSpace = 'pre-wrap';
    hiddenDiv.textContent = content;
    document.body.appendChild(hiddenDiv);

    const lineHeight =
      parseInt(window.getComputedStyle(contentRef.current).lineHeight) ||
      parseInt(window.getComputedStyle(contentRef.current).fontSize);

    const totalHeight = hiddenDiv.offsetHeight;
    const fourLinesHeight = lineHeight * 4;
    setNeedsExpansion(totalHeight > fourLinesHeight);

    fullContentRef.current = totalHeight;

    document.body.removeChild(hiddenDiv);
  }, [content, contentRef.current?.offsetWidth]);

  const onResize = useCallback(() => {
    setExpanded(!expanded);
    apiRef?.current?.resetRowHeights();
  }, [apiRef, expanded]);

  return (
    <div
      data-testid={`${field}-cell`}
      ref={contentRef}
      style={{
        fontSize: '14px',
        letterSpacing: '0.01071em',
        lineHeight: '20px',
        fontFamily: 'Roboto',
        whiteSpace: 'pre-wrap',
        width: '100%',
        maxHeight:
          !expanded && needsExpansion
            ? `${parseInt(window.getComputedStyle(contentRef.current).lineHeight) * 4 || 80}px`
            : 'none',
        overflow: !expanded && needsExpansion ? 'hidden' : 'visible',
        position: 'relative'
      }}
    >
      {content} {needsExpansion && expanded && `\u00A0`}
      {needsExpansion && (
        <Link
          component="button"
          sx={{
            fontSize: 'inherit',
            letterSpacing: 'inherit',
            position: expanded ? 'initial' : 'absolute',
            bottom: 0,
            right: 0,
            padding: expanded ? '0 0 3px 0' : '0 10px',
            zIndex: 1,
            backgroundColor: scGray2,
            '.MuiDataGrid-row:hover  &': {
              backgroundColor: '#F0F0F0'
            }
          }}
          onClick={onResize}
        >
          {expanded ? 'view less' : 'view more'}
        </Link>
      )}
    </div>
  );
};
